import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema } from './schemas/common.js';
import { sourceInstanceSchema, sourceKindDescriptorSchema, sourceKindSchema, } from './schemas/domain.js';
const c = initContract();
/**
 * Every value arrives as a string, because that is what a form produces. The
 * server coerces each one using the descriptor it published, so the panel never
 * has to know that `imapPort` is a number and `imapSecure` is a boolean.
 *
 * An empty secret means "keep the stored one": the panel is never sent a
 * credential, so it cannot send one back, and a blank box must not wipe one.
 */
export const instanceInputSchema = z.object({
    label: z.string().max(120).optional(),
    fields: z.record(z.string(), z.string()),
});
const byId = z.object({ id: z.string().min(1) });
export const sourceInstancesContract = c.router({
    kinds: {
        method: 'GET',
        metadata: access('viewer'),
        path: '/source-kinds',
        summary: 'What can be configured here, and what each one needs to be asked',
        responses: { 200: z.object({ kinds: z.array(sourceKindDescriptorSchema) }) },
    },
    list: {
        method: 'GET',
        metadata: access('viewer'),
        path: '/sources/instances',
        query: z.object({ kind: sourceKindSchema.optional() }),
        responses: { 200: z.object({ instances: z.array(sourceInstanceSchema) }) },
    },
    add: {
        method: 'POST',
        metadata: access('admin'),
        path: '/sources/instances',
        summary: 'Verify the credentials, store them encrypted, start syncing',
        body: instanceInputSchema.extend({ kind: sourceKindSchema }),
        responses: {
            201: z.object({ instance: sourceInstanceSchema }),
            /** The form is wrong: every problem at once, not the first. */
            400: apiErrorSchema.extend({ problems: z.array(z.string()) }),
            /**
             * The provider rejected the credentials. Not 401: that means "your session
             * is gone", and the panel signs out on it.
             */
            422: apiErrorSchema,
            /** The provider could not be reached; nothing was stored. */
            502: apiErrorSchema,
            /** No SECRETS_KEY is configured, so no credential may be stored. */
            503: apiErrorSchema,
        },
    },
    update: {
        method: 'PATCH',
        metadata: access('admin'),
        path: '/sources/instances/:id',
        pathParams: byId,
        body: instanceInputSchema,
        responses: {
            200: z.object({ instance: sourceInstanceSchema }),
            400: apiErrorSchema.extend({ problems: z.array(z.string()) }),
            422: apiErrorSchema,
            404: apiErrorSchema,
            502: apiErrorSchema,
        },
    },
    toggle: {
        method: 'POST',
        metadata: access('admin'),
        path: '/sources/instances/:id/toggle',
        summary: 'Pause or resume syncing, keeping the credentials',
        pathParams: byId,
        body: z.object({}),
        responses: { 200: z.object({ instance: sourceInstanceSchema }), 404: apiErrorSchema },
    },
    test: {
        method: 'POST',
        metadata: access('admin'),
        path: '/sources/instances/:id/test',
        summary: 'Verify the stored credentials again and time it. Nothing is restarted.',
        pathParams: byId,
        body: z.object({}),
        responses: {
            /** A failed test is still a 200: the test ran, and `ok` says how it went. */
            200: z.object({
                ok: z.boolean(),
                latencyMs: z.number().int(),
                error: z.string().optional(),
            }),
            404: apiErrorSchema,
        },
    },
    remove: {
        method: 'DELETE',
        metadata: access('admin'),
        path: '/sources/instances/:id',
        summary: 'Stop syncing and forget the credentials. Records already synced stay searchable.',
        pathParams: byId,
        body: z.object({}),
        responses: { 200: z.object({ instance: sourceInstanceSchema }), 404: apiErrorSchema },
    },
});
//# sourceMappingURL=source-instances.contract.js.map